import clsx from 'clsx';
import React from 'react';

import { assert } from '@gitbook/util-ts';

import type { ButtonKind, ButtonProps } from '../Button';

export interface ButtonGroupProps {
    children: React.ReactNode;

    /**
     * **How buttons in the group are shown in relation to each other.**
     *
     * - **Separated**: Shows the buttons tightly grouped with no gap. This can be used for a split button.
     * - **Segmented**: Show the buttons with a consistent gap between them. This will remove borders on individual buttons.
     */
    kind?: 'separated' | 'segmented';

    /** In which direction buttons are stacked. */
    orientation?: React.AriaAttributes['aria-orientation'];

    /** Show separators of a kind between buttons. Set it to `undefined` to handle separators yourself. Only applicable when `kind` is set to `segmented`. */
    separatorKind?: ButtonKind;

    /** Show border on the container. Only applicable when `kind` is set to `segmented`. */
    border?: boolean;

    /** Whether to wrap buttons when the container gets too big. */
    wrap?: boolean;

    /** If true, disables all the buttons within the group. */
    disabled?: boolean;

    /**
     * **Can be used to optionally style button container.**
     *
     * - Supply one or more classnames from the [Spine tokens](https://github.com/GitbookIO/gitbook-x/tree/main/packages/spine-tokens/src/tokens) (`text-ui-standout`, `text-danger`, ...)
     * - You can also use generic [Tailwind classes](https://tailwindcss.com/docs/utility-first) (`ml-4`, `blur`, ...) if they’re made available in our [config file](https://github.com/GitbookIO/gitbook-x/blob/main/packages/spine-css/tailwind.config.js).
     * - Separate multiple classnames by a space.
     */
    className?: string;
}

function ButtonGroup(
    props: ButtonGroupProps & React.HTMLAttributes<HTMLDivElement>,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const {
        children,
        className,
        disabled = false,
        kind = 'separated',
        separatorKind,
        orientation = 'horizontal',
        wrap = false,
        border = true,
        ...rest
    } = props;

    return (
        <div
            ref={ref}
            role="group"
            data-segmented-button-group={kind === 'segmented' || undefined}
            className={clsx(
                'group relative flex min-w-0 max-w-full aria-[orientation=vertical]:flex-col',
                wrap ? 'flex-wrap' : 'flex-nowrap',
                kind === 'segmented'
                    ? [
                          'size-fit flex-shrink-0 flex-grow-0 rounded-[0.4375rem] *:rounded-none *:border-0 [&>*:focus]:z-10',
                          border && 'border',
                          orientation === 'vertical'
                              ? '[&>*:first-child]:rounded-t-md [&>*:last-child]:rounded-b-md'
                              : '[&>*:first-child]:rounded-l-md [&>*:last-child]:rounded-r-md',
                      ]
                    : 'gap-2',
                className
            )}
            aria-disabled={disabled}
            aria-orientation={orientation}
            {...rest}
        >
            {React.Children.map(children, (child, index) => {
                if (!child) {
                    return;
                }
                assert(
                    React.isValidElement<{
                        disabled: ButtonProps['disabled'];
                        orientation?: React.AriaAttributes['aria-orientation'];
                    }>(child)
                );
                return (
                    <>
                        {kind === 'segmented' && separatorKind && index !== 0 ? (
                            <ButtonGroupSeparator
                                kind={disabled ? 'secondary' : separatorKind}
                                orientation={orientation}
                            />
                        ) : null}
                        {React.cloneElement(child, {
                            disabled: child.props.disabled ?? disabled,
                            ...('label' in child.props ? {} : { orientation }),
                        })}
                    </>
                );
            })}
        </div>
    );
}

/**
 * Multiple [Buttons](https://storybook.gitbook.dev/?path=/docs/spine-button--docs) can be grouped together by wrapping them with a ButtonGroup component.
 *
 * - The buttons should be immediate children of the ButtonGroup component.
 * - The buttons can be stacked in a row (`orientation=horizontal`) or column (`orientation=vertical`).
 */
const _ButtonGroup = React.forwardRef(ButtonGroup);
export { _ButtonGroup as ButtonGroup };

export interface ButtonGroupSeparatorProps {
    kind: ButtonKind;
    orientation?: React.AriaAttributes['aria-orientation'];
}

export function ButtonGroupSeparator(props: ButtonGroupSeparatorProps) {
    const { kind, orientation = 'horizontal' } = props;
    return (
        <span
            className={`block ${
                {
                    primary: 'bg-[var(--sp-color-action-bg-primary-hover)]',
                    merge: 'bg-[var(--sp-color-action-bg-merge-hover)]',
                    danger: 'bg-[var(--sp-color-action-bg-danger-hover)]',
                    upgrade: 'bg-[var(--sp-color-action-bg-upgrade-hover)]',
                    success: 'bg-[var(--sp-color-action-bg-success-hover)]',
                }[kind] ?? 'bg-[var(--sp-color-border-base)]'
            } ${orientation === 'vertical' ? 'h-[1px] w-full' : 'w-[1px]'}`}
        />
    );
}
